'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  PetrohqApiResponseListTankcodeDto,
  TankcodeDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function TankManagement() {
  const { toast } = useToast();
  const [tankData, setTankData] = useState<TankcodeDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 필터 상태 가져오기
  const { store } = useFilterStore();
  const { getAllStores } = useOrganization();

  const fetchTankData = useCallback(async () => {
    setIsLoading(true);
    try {
      // 필터에서 선택된 store 값들을 사용, 없으면 모든 store 사용
      const storeCodeList = store.length > 0 ? store : getAllStores();

      const searchParams = new URLSearchParams();
      storeCodeList.forEach(storeCode => {
        searchParams.append('storeCodeList', storeCode);
      });

      const response = await apiClient.get('master/tank', {
        searchParams: searchParams,
      });
      const data = (await response.json()) as PetrohqApiResponseListTankcodeDto;
      const tankData = data.result || [];
      setTankData(tankData);
    } catch {
      toast({
        title: '조회 실패',
        description: '탱크 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [store, toast, getAllStores]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchTankData();
  }, []);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchTankData();
  };

  return (
    <ExpandableCard
      breadcrumb={
        <Breadcrumb items={menuBreadcrumbItems('tank')} />
      }
      title='탱크 목록'
      fill
      pageHeader={
        <PageHeader
          onSearch={handleSearch}
          isLoading={isLoading}
        />
      }
    >
      {/* 탱크 테이블 */}
      <div
        className='border rounded-lg flex-1 min-h-0'
      >
        <div className='w-full h-full'>
          <RealGridReact
            gridProps={{
              checkBar: { visible: false },
              stateBar: { visible: false },
              edit: { readOnly: true, editable: false },
              sorting: {
                enabled: true,
                showSortOrder: true,
                style: RealGrid.SortStyle.INCLUSIVE,
              },
            }}
            rows={tankData}
          >
            <RGDataField
              fieldName={'compCd'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'compName'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'storeCd'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'storeName'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'tkCode'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'tkType'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'tkOtype'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'tkBefore'}
              dataType={RealGrid.ValueType.NUMBER}
            />
            <RGDataField
              fieldName={'tkTotal'}
              dataType={RealGrid.ValueType.NUMBER}
            />
            <RGDataColumn fieldName={'storeName'} header={{ text: '매장' }} />
            <RGDataColumn
              fieldName={'tkCode'}
              header={{ text: '탱크번호' }}
              width={60}
            />
            <RGDataColumn
              fieldName={'tkType'}
              header={{ text: 'C' }}
              width={60}
              lookupDisplay={true}
              values={['0', '1']}
              labels={['일반', '탱크로리']}
            />
            <RGDataColumn
              fieldName={'tkOtype'}
              header={{ text: '유종' }}
              width={70}
            />
            <RGDataColumn
              fieldName={'tkBefore'}
              header={{ text: '시작재고' }}
              width={90}
              numberFormat={'#,##0.###'}
              styleName={'text-right'}
            />
            <RGDataColumn
              fieldName={'tkTotal'}
              header={{ text: '총량' }}
              width={90}
              numberFormat={'#,##0.###'}
              styleName={'text-right'}
            />
          </RealGridReact>
        </div>
      </div>
    </ExpandableCard>
  );
}
